/**
 * Hooks de TanStack Query.
 *
 * Deliberadamente *no* scoped por sede, a diferencia de cada otra key en
 * `queryKeys.ts`. Ese archivo scopes por sede activa porque el alcance de un
 * usuario cambia y una lista de empleados cacheada no debe sobrevivir al
 * cambio. Una notificación está direccionada al que llama personalmente; no
 * pertenece al filtro de sede, y scopearla tiraría y volvería a pedir el buzón
 * cada vez que alguien cambia un desplegable.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ListParams } from '@/api/types';

import { notificationsApi } from './notificationsApi';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (params: ListParams) => ['notifications', 'list', params] as const,
  unread: () => ['notifications', 'unread'] as const,
};

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unread(),
    queryFn: () => notificationsApi.unreadCount(),
    // El socket es lo que mantiene esto fresco, así que no hay `refetchInterval`.
    // Un poll *y* un socket es lo peor de ambos: el poll enmascara un socket
    // roto, así que nadie nota que el socket está roto hasta el día en que se
    // quita el poll por ser redundante.
    staleTime: 60_000,
  });
}

export function useNotificationList(params: ListParams) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationsApi.list(params),
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids?: string[]) => notificationsApi.markRead(ids),
    onSuccess: (unread) => {
      // La respuesta ya lleva el conteo autoritativo, así que escríbelo directo
      // en vez de invalidar y pagar un segundo viaje por un número que nos
      // acabaron de dar.
      queryClient.setQueryData(notificationKeys.unread(), unread);
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Llamado cuando el buzón pinta una página de notificaciones.
 *
 * Mutación separada de `useMarkRead` porque las dos responden preguntas
 * distintas y la UI las dispara desde lugares distintos — ver el panel.
 */
export function useMarkDelivered() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => notificationsApi.markDelivered(ids),
    onSuccess: (unread) => {
      // El conteo vuelve igual — estar mostrada no es estar leída — pero
      // escribirlo mantiene el badge de esta pestaña al paso si algo más lo
      // movió mientras el request estaba en vuelo.
      queryClient.setQueryData(notificationKeys.unread(), unread);
    },
  });
}
